/**
 * FotosSubir — componente reutilizable de galería con upload + preview + delete.
 *
 * Props:
 *   tipo: 'recepcion_orden' | 'recepcion_turno' | 'recepcion_vehiculo' | 'evidencia_orden'
 *   entidadId: string (id de la orden/turno/vehiculo)
 *   titulo: string (label mostrado arriba)
 *   maxFotos: number (default 8)
 *   readOnly: bool (default false — si true, no muestra botón subir/eliminar)
 *
 * Uso:
 *   <FotosSubir tipo="recepcion_orden" entidadId={orden.id} titulo="Fotos de recepción" />
 *
 * Feature gating: si el tenant no tiene la feature, backend devuelve 402
 * y el componente muestra CTA a /suscripcion.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { Camera, Trash2, X, Loader2, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/contexto/AuthContexto';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function FotosSubir({ tipo, entidadId, titulo = 'Fotos', maxFotos = 8, readOnly = false }) {
  const { featuresPlan } = useAuth();
  const [fotos, setFotos] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [subiendo, setSubiendo] = useState(false);
  const [preview, setPreview] = useState(null); // {id, url}
  const fileRef = useRef(null);
  const token = localStorage.getItem('token');

  // Determinar la feature key según el tipo
  const featureKey = tipo === 'evidencia_orden' ? 'evidencias_fotograficas' : 'fotos_recepcion';
  const tieneFeature = featuresPlan === null || featuresPlan.includes(featureKey);

  const cargar = useCallback(async () => {
    if (!entidadId) return;
    setCargando(true);
    try {
      const r = await api.get(`/fotos/entidad/${tipo}/${entidadId}`);
      setFotos(r.data || []);
    } catch (e) {
      // silencioso
    } finally {
      setCargando(false);
    }
  }, [tipo, entidadId]);

  useEffect(() => { cargar(); }, [cargar]);

  const onFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (fotos.length + files.length > maxFotos) {
      toast.error(`Máximo ${maxFotos} fotos`);
      return;
    }
    setSubiendo(true);
    for (const f of files) {
      const fd = new FormData();
      fd.append('file', f);
      fd.append('tipo', tipo);
      fd.append('entidad_id', entidadId);
      try {
        await api.post('/fotos/subir', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      } catch (err) {
        const detail = err.response?.data?.detail || 'Error subiendo foto';
        if (err.response?.status === 402) {
          toast.error(detail, { duration: 6000 });
          break;
        }
        toast.error(`${f.name}: ${detail}`);
      }
    }
    setSubiendo(false);
    fileRef.current.value = '';
    cargar();
    toast.success('Fotos subidas');
  };

  const eliminar = async (id) => {
    if (!window.confirm('¿Eliminar esta foto?')) return;
    try {
      await api.delete(`/fotos/${id}`);
      setFotos(fs => fs.filter(f => f.id !== id));
      toast.success('Foto eliminada');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const urlFoto = (id) => `${API}/fotos/${id}?auth=${encodeURIComponent(token)}`;

  if (!tieneFeature && !readOnly) {
    return (
      <div className="bg-slate-900 border border-slate-800 rounded-lg p-4" data-testid={`fotos-locked-${tipo}`}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center">
            <Lock className="w-4 h-4 text-amber-400" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-slate-200">{titulo}</p>
            <p className="text-xs text-slate-500">Función no incluida en tu plan. Actualiza tu suscripción para desbloquearla.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-lg p-4" data-testid={`fotos-${tipo}`}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-sm font-semibold text-slate-200">{titulo}</p>
          <p className="text-[11px] text-slate-500">{fotos.length}/{maxFotos} · JPG/PNG/WEBP · max 8MB c/u</p>
        </div>
        {!readOnly && fotos.length < maxFotos && (
          <Button size="sm" onClick={() => fileRef.current?.click()} disabled={subiendo}
            className="bg-cyan-500 hover:bg-cyan-600 text-black font-semibold h-8"
            data-testid={`btn-subir-${tipo}`}>
            {subiendo ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Camera className="w-3.5 h-3.5 mr-1" />}
            {subiendo ? 'Subiendo...' : 'Subir'}
          </Button>
        )}
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp"
          multiple className="hidden" onChange={onFileChange} data-testid={`input-file-${tipo}`} />
      </div>

      {cargando ? (
        <div className="py-6 text-center text-xs text-slate-500">Cargando...</div>
      ) : fotos.length === 0 ? (
        <div className="py-6 text-center text-xs text-slate-500 border border-dashed border-slate-800 rounded">
          Sin fotos aún. Sube evidencias para tener registro visual.
        </div>
      ) : (
        <div className="grid grid-cols-4 gap-2">
          {fotos.map(f => (
            <div key={f.id} className="relative group aspect-square bg-slate-950 border border-slate-800 rounded overflow-hidden">
              <img src={urlFoto(f.id)} alt="Foto"
                className="w-full h-full object-cover cursor-pointer hover:opacity-80 transition"
                onClick={() => setPreview({ id: f.id, url: urlFoto(f.id) })} />
              {!readOnly && (
                <button onClick={() => eliminar(f.id)}
                  className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 bg-red-500 hover:bg-red-600 text-white rounded p-1 transition"
                  data-testid={`eliminar-foto-${f.id}`}>
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setPreview(null)} data-testid="foto-preview-modal">
          <button className="absolute top-4 right-4 text-white bg-slate-800 rounded-full p-2 hover:bg-slate-700"
            onClick={() => setPreview(null)}>
            <X className="w-5 h-5" />
          </button>
          <img src={preview.url} alt="Preview" className="max-w-full max-h-full object-contain" />
        </div>
      )}
    </div>
  );
}
